/**
 * GTM data layer - interaction events, ported from the legacy theme (scripts-body-end-unique.liquid).
 *
 * Page-load events and window.data_layer_info / window.CBI_ANALYTICS are defined in
 * snippets/analytics-data-layer.liquid, which renders before this file.
 *
 * Cart events hook into the CBI cart (assets/cbi-cart.js), which every add, quantity change,
 * open and checkout in this theme goes through:
 *   - cart:add              (CustomEvent, detail: { items, requested }) -> add_to_cart
 *   - cbi-cart:line-change  (detail: { id, quantity, cart })            -> remove_from_cart
 *   - cbi-cart:open                                                      -> view_cart
 *   - cbi-cart:checkout                                                  -> begin_checkout
 */
(function () {
  'use strict';

  const config = window.CBI_ANALYTICS || {};
  const info = () => window.data_layer_info || {};
  const cents = (value) => (Number(value) || 0) / 100;

  function push(payload, isEcommerce) {
    window.dataLayer = window.dataLayer || [];
    if (isEcommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }

  // ---------------------------------------------------------------------------
  // Cart snapshot - needed to know what was removed and to push begin_checkout
  // synchronously right before the browser navigates to /checkout.
  // ---------------------------------------------------------------------------
  let cartSnapshot = null;

  function refreshCartSnapshot() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        cartSnapshot = cart;
        return cart;
      })
      .catch(() => cartSnapshot);
  }

  /** CBI cart passes either the line item key or the variant id as the line identifier. */
  function findLine(cart, id) {
    if (!cart || !cart.items) return null;
    const target = String(id);
    return cart.items.find((line) => line.key === target || String(line.variant_id) === target) || null;
  }

  // ---------------------------------------------------------------------------
  // Product card list context, so a quick add from a card keeps its list info.
  // ---------------------------------------------------------------------------
  let lastCardContext = null;

  function getCardData(card) {
    const section = card.closest('.shopify-section') || document;
    const cards = Array.from(section.querySelectorAll('product-card[data-analytics-item-id]'));
    return {
      index: cards.indexOf(card),
      item_id: card.dataset.analyticsItemId,
      item_name: card.dataset.analyticsItemName,
      item_variant: card.dataset.analyticsItemVariant,
      price: card.dataset.analyticsItemPrice,
      item_list_id: card.dataset.analyticsListId || null,
      item_list_name: card.dataset.analyticsListName || null,
    };
  }

  /**
   * About : Selected product tracking (collection, search, home, recommendations)
   * Description : Fired when a product card is clicked (not its quick add / add to cart buttons)
   */
  function DL__select_item(cardData) {
    push(
      {
        event: 'select_item',
        currency: config.currency,
        template_name: 'product-list',
        ecommerce: {
          items: [
            {
              discount: 0,
              index: cardData.index,
              item_category: cardData.item_list_name,
              item_id: cardData.item_id,
              item_list_id: cardData.item_list_id,
              item_list_name: cardData.item_list_name,
              item_name: cardData.item_name,
              item_variant: cardData.item_variant,
              price: cardData.price,
              quantity: 1,
            },
          ],
        },
      },
      true
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const card = target && target.closest('product-card[data-analytics-item-id]');
      if (!card) return;

      const cardData = getCardData(card);
      lastCardContext = { ...cardData, timestamp: Date.now() };

      // Clicks on the card's buttons (quick add, variant swatches, add to cart) are not a selection.
      if (target.closest('button, input, select, label, quick-add-component, product-form-component')) return;

      DL__select_item(cardData);
    },
    true
  );

  // ---------------------------------------------------------------------------
  // add_to_cart
  // ---------------------------------------------------------------------------
  function parseRequestedQuantities(requested) {
    const quantities = {};
    try {
      const body = typeof requested === 'string' ? JSON.parse(requested) : requested;
      const items = (body && body.items) || (body && body.id ? [body] : []);
      items.forEach((item) => {
        quantities[String(item.id)] = (quantities[String(item.id)] || 0) + (parseInt(item.quantity, 10) || 1);
      });
    } catch (e) {
      // Fall back to the quantities in the /cart/add.js response.
    }
    return quantities;
  }

  /**
   * About : Adding products to cart
   * Description : Fired after /cart/add.js succeeds (PDP, quick add, product cards)
   */
  function DL__add_to_cart(detail) {
    const lines = (detail && detail.items) || [];
    if (!lines.length) return;

    const requested = parseRequestedQuantities(detail.requested);
    const context =
      lastCardContext && Date.now() - lastCardContext.timestamp < 30000 ? lastCardContext : null;

    let value = 0;
    const items = lines.map((line, index) => {
      const quantity = requested[String(line.variant_id)] || requested[String(line.id)] || line.quantity;
      const price = cents(line.final_price || line.price);
      const fromCard = context && String(context.item_id) === String(line.product_id);
      value += price * quantity;

      return {
        discount: cents(line.total_discount),
        index: fromCard ? context.index : index,
        item_category: line.product_type,
        item_id: line.product_id,
        item_list_id: fromCard ? context.item_list_id : null,
        item_list_name: fromCard ? context.item_list_name : null,
        item_name: line.product_title,
        item_variant: line.variant_id,
        price,
        quantity,
      };
    });

    push(
      {
        event: 'add_to_cart',
        currency: config.currency,
        template_name: config.templateName,
        user_id: info().user_id,
        ecommerce: {
          value: Math.round(value * 100) / 100,
          items,
        },
      },
      true
    );
  }

  document.addEventListener('cart:add', (event) => {
    DL__add_to_cart(event.detail);
    refreshCartSnapshot();
  });

  // ---------------------------------------------------------------------------
  // remove_from_cart
  // ---------------------------------------------------------------------------
  /**
   * About : item tracking
   * Description : Fired when a cart line quantity goes down (trash icon or "-" in the CBI cart)
   */
  function DL__remove_from_cart(previousCart, detail) {
    const previousLine = findLine(previousCart, detail.id);
    if (!previousLine) return;

    const currentLine = findLine(detail.cart, detail.id);
    const removedQuantity = previousLine.quantity - (currentLine ? currentLine.quantity : 0);
    if (removedQuantity <= 0) return;

    const index = previousCart.items.indexOf(previousLine);
    const price = cents(previousLine.final_price || previousLine.price);

    push(
      {
        event: 'remove_from_cart',
        currency: config.currency,
        template_name: 'cart-page',
        user_id: info().user_id,
        ecommerce: {
          value: Math.round(price * removedQuantity * 100) / 100,
          items: [
            {
              discount: 0,
              index,
              item_category: previousLine.product_type,
              item_id: previousLine.product_id,
              item_list_id: null,
              item_list_name: null,
              item_name: previousLine.product_title,
              item_variant: previousLine.variant_id,
              price,
              quantity: removedQuantity,
            },
          ],
        },
      },
      true
    );
  }

  document.addEventListener('cbi-cart:line-change', (event) => {
    const detail = event.detail || {};
    const previousCart = cartSnapshot;
    if (detail.cart && detail.cart.items) cartSnapshot = detail.cart;

    if (previousCart) {
      DL__remove_from_cart(previousCart, detail);
    } else {
      refreshCartSnapshot();
    }
  });

  document.addEventListener('cart:updated', () => {
    refreshCartSnapshot();
  });

  // ---------------------------------------------------------------------------
  // view_cart
  // ---------------------------------------------------------------------------
  /**
   * About : cart tracking
   * Description : Fired when the CBI cart drawer opens (cart icon, /#cart, after add to cart)
   */
  function DL__view_cart(cart) {
    const items = cart.items.map((line, index) => ({
      discount: line.total_discount,
      index,
      item_category: line.product_type,
      item_id: line.id,
      item_list_id: null,
      item_list_name: line.product_type,
      item_name: line.title,
      item_variant: line.variant_id,
      price: cents(line.price),
      quantity: line.quantity,
    }));

    push(
      {
        event: 'view_cart',
        currency: config.currency,
        template_name: 'cart-page',
        user_id: info().user_id,
        ecommerce: {
          value: cents(cart.total_price),
          items,
        },
      },
      true
    );
  }

  document.addEventListener('cbi-cart:open', () => {
    refreshCartSnapshot().then((cart) => {
      if (cart && cart.items) DL__view_cart(cart);
    });
  });

  // ---------------------------------------------------------------------------
  // begin_checkout
  // ---------------------------------------------------------------------------
  /**
   * About : checkout tracking
   * Description : Fired when the CBI cart checkout button sends the customer to /checkout
   */
  function DL__begin_checkout(cart) {
    const items = cart.items.map((line, index) => ({
      currency: config.currency,
      discount: line.total_discount,
      index,
      item_category: line.product_type,
      item_id: line.id,
      item_list_id: null,
      item_list_name: line.product_type,
      item_name: line.title,
      item_variant: line.variant_id,
      price: cents(line.price),
      quantity: line.quantity,
      affiliation: line.vendor,
    }));

    push(
      {
        event: 'begin_checkout',
        currency: config.currency,
        template_name: 'checkout-shipping-page',
        user_id: info().user_id,
        membership_level: info().membership_level,
        ecommerce: {
          items,
        },
      },
      true
    );
  }

  document.addEventListener('cbi-cart:checkout', () => {
    if (cartSnapshot && cartSnapshot.items) DL__begin_checkout(cartSnapshot);
  });

  // ---------------------------------------------------------------------------
  // newsletter_subscription (Klaviyo)
  // ---------------------------------------------------------------------------
  /**
   * About : newsletter subscription tracking
   * Description : Fired when a Klaviyo signup form is successfully submitted
   */
  function DL__newsletter_subscription(pageType) {
    push({
      event: 'newsletter_subscription',
      template_name: pageType,
    });
  }

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    if (detail.type !== 'submit') return;

    const formIds = config.klaviyoNewsletterFormIds || [];
    if (formIds.length && formIds.indexOf(detail.formId) === -1) return;

    DL__newsletter_subscription('newsletter-signup');
  });

  // ---------------------------------------------------------------------------
  // view_item_list - re-fired after AJAX filtering / sorting on collection pages
  // ---------------------------------------------------------------------------
  function DL__view_item_list_from_cards(container) {
    const cards = Array.from(container.querySelectorAll('product-card[data-analytics-item-id]'));
    if (!cards.length) return;

    const listName = cards[0].dataset.analyticsListName || null;

    push(
      {
        event: 'view_item_list',
        currency: config.currency,
        search_term: listName,
        template_name: 'product-list',
        user_id: info().user_id,
        membership_level: info().membership_level,
        ecommerce: {
          items: cards.map((card, index) => ({
            discount: 0,
            index: index + 1,
            item_id: Number(card.dataset.analyticsItemId) || card.dataset.analyticsItemId,
            item_list_id: Number(card.dataset.analyticsListId) || null,
            item_list_name: listName,
            item_name: card.dataset.analyticsItemName,
            item_variant: Number(card.dataset.analyticsItemVariant) || card.dataset.analyticsItemVariant,
            price: card.dataset.analyticsItemPriceRounded,
            quantity: 1,
          })),
        },
      },
      true
    );
  }

  document.addEventListener('shopify:collection:update', (event) => {
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then(() => {
        const section = document.querySelector('main .product-grid, main [data-product-grid], main');
        DL__view_item_list_from_cards(section || document);
      })
      .catch(() => {});
  });

  refreshCartSnapshot();
})();
